import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { db } from "../firebaseConfig";
import "./Contracts.css";

const emptyContract = {
  siteName: "",
  propertyId: "",
  contractTitle: "",
  serviceType: "",
  signatureDate: "",
  initialAmount: "",
  feeSchedule: "Monthly",
  startDate: "",
  endDate: "",
  status: "Active",
  amount: "",
  notes: "",
  files: [],
};


const getServiceCode = (value = "") => String(value || "").trim().split(/\s+-\s+|\s+/)[0].toUpperCase();
const readPropertyPricing = (property = {}, serviceType = "") => {
  const code = getServiceCode(serviceType);
  const overrides = property?.servicePricingOverrides || {};
  const override = overrides[code] || overrides[serviceType] || {};
  const legacy = property?.serviceTypeTotals || property?.serviceTotals || {};
  return {
    amount: override.amount ?? override.total ?? legacy[code] ?? legacy[serviceType] ?? "",
    initialAmount: override.initialAmount ?? "",
    feeSchedule: override.feeSchedule || "",
  };
};
const resolveServicePricing = (property = {}, template = {}, serviceType = "") => {
  const override = readPropertyPricing(property, serviceType);
  return {
    amount: override.amount !== "" && override.amount != null ? override.amount : (template?.total ?? ""),
    initialAmount: override.initialAmount !== "" && override.initialAmount != null ? override.initialAmount : (template?.initialAmount ?? ""),
    feeSchedule: override.feeSchedule || template?.feeSchedule || "Monthly",
  };
};

const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [serviceTypeTemplates, setServiceTypeTemplates] = useState([]);
  const [newContract, setNewContract] = useState(emptyContract);
  const [selectedContract, setSelectedContract] = useState(null);
  const [editedContract, setEditedContract] = useState(emptyContract);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [siteQuery, setSiteQuery] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [sortBy, setSortBy] = useState("siteName");
  const [sortDir, setSortDir] = useState("asc");

  const storage = getStorage();

  useEffect(() => {
    fetchContracts();
    Promise.all([getDocs(collection(db, "properties")), getDocs(collection(db, "proposalsPPDServiceTypes"))]).then(([p, t]) => {
      setProperties(p.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a,b) => String(a.propertyName || "").localeCompare(String(b.propertyName || ""))));
      setServiceTypeTemplates(t.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  const fetchContracts = async () => {
    try {
      const contractsRef = collection(db, "contracts");
      const q = query(contractsRef, orderBy("siteName"));
      const snapshot = await getDocs(q);

      const list = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      setContracts(list);
    } catch (error) {
      console.error("Error fetching contracts:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewContract((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditedContract((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyContractSelection = (setter, current, patch) => {
    const next = { ...current, ...patch };
    const property = properties.find((p) => p.propertyName === next.siteName || p.id === next.propertyId);
    const template = serviceTypeTemplates.find((t) => t.serviceType === next.serviceType);
    if (property && template && (patch.siteName !== undefined || patch.serviceType !== undefined)) {
      const pricing = resolveServicePricing(property, template, next.serviceType);
      Object.assign(next, { propertyId: property.id, contractTitle: next.serviceType, amount: pricing.amount, initialAmount: pricing.initialAmount, feeSchedule: pricing.feeSchedule });
    }
    setter(next);
  };

  const persistContractOverride = async (contract, field, rawValue) => {
    const property = properties.find((p) => p.id === contract.propertyId || p.propertyName === contract.siteName);
    if (!property?.id || !contract.serviceType) return;
    const code = getServiceCode(contract.serviceType);
    const current = readPropertyPricing(property, contract.serviceType);
    const value = field === "feeSchedule" ? rawValue : (rawValue === "" ? "" : Number(rawValue));
    const next = { ...current, [field]: value };
    await updateDoc(doc(db, "properties", property.id), { [`servicePricingOverrides.${code}`]: next });
    setProperties((prev) => prev.map((p) => p.id === property.id ? { ...p, servicePricingOverrides: { ...(p.servicePricingOverrides || {}), [code]: next } } : p));
  };

  const uploadFiles = async (files, siteName) => {
    const uploadedFiles = [];

    for (const file of files) {
      const safeSiteName = (siteName || "unknown-site")
        .trim()
        .replace(/[^a-zA-Z0-9-_]/g, "_");

      const filePath = `contracts/${safeSiteName}/${Date.now()}-${file.name}`;
      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      uploadedFiles.push({
        fileName: file.name,
        fileURL: downloadURL,
        uploadedAt: new Date().toISOString(),
      });
    }

    return uploadedFiles;
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      setSaving(true);
      const uploadedFiles = await uploadFiles(files, newContract.siteName);

      setNewContract((prev) => ({
        ...prev,
        files: [...prev.files, ...uploadedFiles],
      }));
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("There was an error uploading the contract files.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      setSaving(true);
      const uploadedFiles = await uploadFiles(files, editedContract.siteName);

      setEditedContract((prev) => ({
        ...prev,
        files: [...(prev.files || []), ...uploadedFiles],
      }));
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("There was an error uploading the contract files.");
    } finally {
      setSaving(false);
    }
  };

  const validateContract = (contract) => {
    if (!contract.siteName.trim()) {
      alert("Please enter the site name.");
      return false;
    }


    if (contract.startDate && contract.endDate && contract.endDate < contract.startDate) {
      alert("End date cannot be before start date.");
      return false;
    }

    return true;
  };

  const handleAddContract = async () => {
    if (!validateContract(newContract)) return;

    try {
      setSaving(true);

      const payload = {
        ...newContract,
        siteNameLower: newContract.siteName.trim().toLowerCase(),
        serviceType: newContract.serviceType || newContract.contractTitle,
        contractTitle: newContract.serviceType || newContract.contractTitle,
        recurringInvoiceAmount: Number(newContract.amount || 0),
        recurringFeeSchedule: newContract.feeSchedule || "Monthly",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, "contracts"), payload);

      const savedContract = {
        id: docRef.id,
        ...payload,
      };

      setContracts((prev) => [...prev, savedContract]);
      setNewContract(emptyContract);
      setShowAddModal(false);
    } catch (error) {
      console.error("Error adding contract:", error);
      alert("There was an error saving the contract.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!validateContract(editedContract)) return;

    try {
      setSaving(true);

      const contractRef = doc(db, "contracts", editedContract.id);

      const payload = {
        ...editedContract,
        siteNameLower: editedContract.siteName.trim().toLowerCase(),
        serviceType: editedContract.serviceType || editedContract.contractTitle,
        contractTitle: editedContract.serviceType || editedContract.contractTitle,
        recurringInvoiceAmount: Number(editedContract.amount || 0),
        recurringFeeSchedule: editedContract.feeSchedule || "Monthly",
        updatedAt: serverTimestamp(),
      };

      await updateDoc(contractRef, payload);

      setContracts((prev) =>
        prev.map((contract) =>
          contract.id === editedContract.id ? { ...payload } : contract
        )
      );

      setSelectedContract(payload);
      setIsEditing(false);
      setShowDetailsModal(false);
      setSelectedContract(null);
    } catch (error) {
      console.error("Error updating contract:", error);
      alert("There was an error updating the contract.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContract = async () => {
    if (!selectedContract?.id) return;

    try {
      await deleteDoc(doc(db, "contracts", selectedContract.id));

      setContracts((prev) =>
        prev.filter((contract) => contract.id !== selectedContract.id)
      );

      setShowDeleteConfirmation(false);
      setShowDetailsModal(false);
      setSelectedContract(null);
      setIsEditing(false);
    } catch (error) {
      console.error("Error deleting contract:", error);
      alert("There was an error deleting the contract.");
    }
  };

  const openDetails = (contract) => {
    setSelectedContract(contract);
    setEditedContract(contract);
    setIsEditing(false);
    setShowDetailsModal(true);
  };

  const closeAllModals = () => {
    setShowAddModal(false);
    setShowDetailsModal(false);
    setShowDeleteConfirmation(false);
    setSelectedContract(null);
    setEditedContract(emptyContract);
    setIsEditing(false);
  };

  const sortArrow = (key) =>
    sortBy === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const contractOverlapsRange = (contract, searchStart, searchEnd) => {
    const contractStart = contract.startDate || "";
    const contractEnd = contract.endDate || "";

    if (!searchStart && !searchEnd) return true;
    if (!contractStart || !contractEnd) return false;

    if (searchStart && searchEnd) {
      return contractStart <= searchEnd && contractEnd >= searchStart;
    }

    if (searchStart) {
      return contractEnd >= searchStart;
    }

    if (searchEnd) {
      return contractStart <= searchEnd;
    }

    return true;
  };

  const filteredContracts = useMemo(() => {
    const site = siteQuery.trim().toLowerCase();

    return [...contracts]
      .filter((contract) => {
        const matchesSite = (contract.siteName || "")
          .toLowerCase()
          .includes(site);

        const matchesStatus = statusFilter
          ? contract.status === statusFilter
          : true;

        const matchesDate = contractOverlapsRange(
          contract,
          rangeStart,
          rangeEnd
        );

        return matchesSite && matchesStatus && matchesDate;
      })
      .sort((a, b) => {
        const av = (a[sortBy] || "").toString().toLowerCase();
        const bv = (b[sortBy] || "").toString().toLowerCase();

        const result = av.localeCompare(bv, undefined, {
          sensitivity: "base",
        });

        return sortDir === "asc" ? result : -result;
      });
  }, [contracts, siteQuery, rangeStart, rangeEnd, statusFilter, sortBy, sortDir]);

  return (
    <div className="contracts-page">
      <div className="contracts-header">
        <div>
          <h1>Contracts</h1>
          <p className="contracts-subtitle">
            Upload, track, and search all site contracts in one place.
          </p>
        </div>

        <button className="primary-button" onClick={() => setShowAddModal(true)}>
          + Add Contract
        </button>
      </div>

      <div className="contracts-filters">
        <input
          type="text"
          placeholder="Search by site name..."
          value={siteQuery}
          onChange={(e) => setSiteQuery(e.target.value)}
        />

        <div className="date-filter-group">
          <label>From</label>
          <input
            type="date"
            value={rangeStart}
            onChange={(e) => setRangeStart(e.target.value)}
          />
        </div>

        <div className="date-filter-group">
          <label>To</label>
          <input
            type="date"
            value={rangeEnd}
            onChange={(e) => setRangeEnd(e.target.value)}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Expired">Expired</option>
          <option value="Pending">Pending</option>
          <option value="Terminated">Terminated</option>
        </select>

        <button
          className="secondary-button"
          onClick={() => {
            setSiteQuery("");
            setRangeStart("");
            setRangeEnd("");
            setStatusFilter("");
          }}
        >
          Clear
        </button>
      </div>

      <div className="contracts-summary">
        <div className="summary-card">
          <span>Total Contracts</span>
          <strong>{filteredContracts.length}</strong>
        </div>
        <div className="summary-card">
          <span>Active</span>
          <strong>
            {filteredContracts.filter((c) => c.status === "Active").length}
          </strong>
        </div>
        <div className="summary-card">
          <span>Pending</span>
          <strong>
            {filteredContracts.filter((c) => c.status === "Pending").length}
          </strong>
        </div>
        <div className="summary-card">
          <span>Expired</span>
          <strong>
            {filteredContracts.filter((c) => c.status === "Expired").length}
          </strong>
        </div>
      </div>

      <div className="table-wrap">
        <table className="contracts-table">
          <thead>
            <tr>
              <th>
                <button className="table-sort" onClick={() => handleSort("siteName")}>
                  Site Name{sortArrow("siteName")}
                </button>
              </th>
              <th>
                <button className="table-sort" onClick={() => handleSort("contractTitle")}>
                  Contract Title{sortArrow("contractTitle")}
                </button>
              </th>
              <th>
                <button className="table-sort" onClick={() => handleSort("vendorName")}>
                  Vendor{sortArrow("vendorName")}
                </button>
              </th>
              <th>
                <button className="table-sort" onClick={() => handleSort("startDate")}>
                  Start Date{sortArrow("startDate")}
                </button>
              </th>
              <th>
                <button className="table-sort" onClick={() => handleSort("endDate")}>
                  End Date{sortArrow("endDate")}
                </button>
              </th>
              <th>
                <button className="table-sort" onClick={() => handleSort("status")}>
                  Status{sortArrow("status")}
                </button>
              </th>
              <th>Files</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredContracts.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-row">
                  No contracts found.
                </td>
              </tr>
            ) : (
              filteredContracts.map((contract) => (
                <tr key={contract.id}>
                  <td>{contract.siteName || "-"}</td>
                  <td>{contract.contractTitle || "-"}</td>
                  <td>{contract.vendorName || "-"}</td>
                  <td>{contract.startDate || "-"}</td>
                  <td>{contract.endDate || "-"}</td>
                  <td>
                    <span className={`status-pill status-${(contract.status || "").toLowerCase()}`}>
                      {contract.status || "-"}
                    </span>
                  </td>
                  <td>{contract.files?.length || 0}</td>
                  <td>
                    <button
                      className="row-button"
                      onClick={() => openDetails(contract)}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal contract-modal">
            <h2>Add Contract</h2>

            <div className="form-grid">
              <div><label>Property / Client Name *</label><select value={newContract.siteName} onChange={(e) => applyContractSelection(setNewContract, newContract, { siteName: e.target.value, propertyId: properties.find((p) => p.propertyName === e.target.value)?.id || "" })}><option value="">Select Property</option>{properties.map((p) => <option key={p.id} value={p.propertyName}>{p.propertyName}</option>)}</select></div>
              <div><label>Service Type</label><select value={newContract.serviceType} onChange={(e) => applyContractSelection(setNewContract, newContract, { serviceType: e.target.value, contractTitle: e.target.value })}><option value="">Select Service Type</option>{serviceTypeTemplates.map((t) => <option key={t.id} value={t.serviceType}>{t.serviceType}</option>)}</select></div>
              <div><label>Signature Date</label><input type="date" name="signatureDate" value={newContract.signatureDate || ""} onChange={handleInputChange} /></div>

              <div>
                <label>Status</label>
                <select
                  name="status"
                  value={newContract.status}
                  onChange={handleInputChange}
                >
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Expired">Expired</option>
                  <option value="Terminated">Terminated</option>
                </select>
              </div>

              <div>
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={newContract.startDate}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={newContract.endDate}
                  onChange={handleInputChange}
                />
              </div>

              <div><label>Recurring Amount</label><input type="number" step="0.01" name="amount" value={newContract.amount} onChange={handleInputChange} onBlur={(e) => persistContractOverride(newContract, "amount", e.target.value)} /></div>
              <div><label>Initial Amount</label><input type="number" step="0.01" name="initialAmount" value={newContract.initialAmount || ""} onChange={handleInputChange} onBlur={(e) => persistContractOverride(newContract, "initialAmount", e.target.value)} /></div>
              <div><label>Fee Schedule</label><select name="feeSchedule" value={newContract.feeSchedule || "Monthly"} onChange={handleInputChange} onBlur={(e) => persistContractOverride(newContract, "feeSchedule", e.target.value)}>{["Monthly","Quarterly","Every 6 Months","Yearly","One-Time","Other"].map((o)=><option key={o} value={o}>{o}</option>)}</select></div>

              <div className="full-width">
                <label>Notes</label>
                <textarea
                  name="notes"
                  rows="4"
                  value={newContract.notes}
                  onChange={handleInputChange}
                />
              </div>

              <div className="full-width">
                <label>Upload Contract Files</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                  onChange={handleFileUpload}
                />

                {newContract.files?.length > 0 && (
                  <div className="file-list">
                    {newContract.files.map((file, index) => (
                      <div key={index} className="file-chip">
                        <a href={file.fileURL} target="_blank" rel="noreferrer">
                          {file.fileName}
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-buttons">
              <button className="primary-button" onClick={handleAddContract} disabled={saving}>
                {saving ? "Saving..." : "Save Contract"}
              </button>
              <button className="secondary-button" onClick={closeAllModals}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && selectedContract && (
        <div className="modal-overlay">
          <div className="modal contract-modal">
            <h2>Contract Details</h2>

            {isEditing ? (
              <>
                <div className="form-grid">
                  <div><label>Property / Client Name</label><select value={editedContract.siteName || ""} onChange={(e) => applyContractSelection(setEditedContract, editedContract, { siteName: e.target.value, propertyId: properties.find((p) => p.propertyName === e.target.value)?.id || "" })}><option value="">Select Property</option>{properties.map((p) => <option key={p.id} value={p.propertyName}>{p.propertyName}</option>)}</select></div>
                  <div><label>Service Type</label><select value={editedContract.serviceType || editedContract.contractTitle || ""} onChange={(e) => applyContractSelection(setEditedContract, editedContract, { serviceType: e.target.value, contractTitle: e.target.value })}><option value="">Select Service Type</option>{serviceTypeTemplates.map((t) => <option key={t.id} value={t.serviceType}>{t.serviceType}</option>)}</select></div>
                  <div><label>Signature Date</label><input type="date" name="signatureDate" value={editedContract.signatureDate || ""} onChange={handleEditInputChange} /></div>

                  <div>
                    <label>Status</label>
                    <select
                      name="status"
                      value={editedContract.status || "Active"}
                      onChange={handleEditInputChange}
                    >
                      <option value="Active">Active</option>
                      <option value="Pending">Pending</option>
                      <option value="Expired">Expired</option>
                      <option value="Terminated">Terminated</option>
                    </select>
                  </div>

                  <div>
                    <label>Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      value={editedContract.startDate || ""}
                      onChange={handleEditInputChange}
                    />
                  </div>

                  <div>
                    <label>End Date</label>
                    <input
                      type="date"
                      name="endDate"
                      value={editedContract.endDate || ""}
                      onChange={handleEditInputChange}
                    />
                  </div>

                  <div><label>Recurring Amount</label><input type="number" step="0.01" name="amount" value={editedContract.amount || ""} onChange={handleEditInputChange} onBlur={(e) => persistContractOverride(editedContract, "amount", e.target.value)} /></div>
                  <div><label>Initial Amount</label><input type="number" step="0.01" name="initialAmount" value={editedContract.initialAmount || ""} onChange={handleEditInputChange} onBlur={(e) => persistContractOverride(editedContract, "initialAmount", e.target.value)} /></div>
                  <div><label>Fee Schedule</label><select name="feeSchedule" value={editedContract.feeSchedule || "Monthly"} onChange={handleEditInputChange} onBlur={(e) => persistContractOverride(editedContract, "feeSchedule", e.target.value)}>{["Monthly","Quarterly","Every 6 Months","Yearly","One-Time","Other"].map((o)=><option key={o} value={o}>{o}</option>)}</select></div>

                  <div className="full-width">
                    <label>Notes</label>
                    <textarea
                      rows="4"
                      name="notes"
                      value={editedContract.notes || ""}
                      onChange={handleEditInputChange}
                    />
                  </div>

                  <div className="full-width">
                    <label>Add More Files</label>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                      onChange={handleEditFileUpload}
                    />

                    {editedContract.files?.length > 0 && (
                      <div className="file-list">
                        {editedContract.files.map((file, index) => (
                          <div key={index} className="file-chip">
                            <a
                              href={file.fileURL}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {file.fileName}
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-buttons">
                  <button className="primary-button" onClick={handleSaveChanges} disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    className="secondary-button"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="details-grid">
                  <div><strong>Property / Client Name:</strong> {selectedContract.siteName || "-"}</div>
                  <div><strong>Service Type:</strong> {selectedContract.serviceType || selectedContract.contractTitle || "-"}</div>
                  <div><strong>Signature Date:</strong> {selectedContract.signatureDate || "-"}</div>
                  <div><strong>Status:</strong> {selectedContract.status || "-"}</div>
                  <div><strong>Start Date:</strong> {selectedContract.startDate || "-"}</div>
                  <div><strong>End Date:</strong> {selectedContract.endDate || "-"}</div>
                  <div><strong>Recurring Amount:</strong> {selectedContract.amount || "-"}</div><div><strong>Initial Amount:</strong> {selectedContract.initialAmount || "-"}</div><div><strong>Fee Schedule:</strong> {selectedContract.feeSchedule || "-"}</div>
                </div>

                <div className="details-section">
                  <h3>Notes</h3>
                  <p>{selectedContract.notes || "No notes added."}</p>
                </div>

                <div className="details-section">
                  <h3>Files</h3>
                  {selectedContract.files?.length ? (
                    <div className="file-list">
                      {selectedContract.files.map((file, index) => (
                        <div key={index} className="file-chip">
                          <a href={file.fileURL} target="_blank" rel="noreferrer">
                            {file.fileName}
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No files uploaded.</p>
                  )}
                </div>

                <div className="modal-buttons">
                  <button
                    className="primary-button"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </button>
                  <button
                    className="danger-button"
                    onClick={() => setShowDeleteConfirmation(true)}
                  >
                    Delete
                  </button>
                  <button className="secondary-button" onClick={closeAllModals}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showDeleteConfirmation && (
        <div className="modal-overlay">
          <div className="modal confirm-modal">
            <h2>Delete Contract?</h2>
            <p>This action cannot be undone.</p>
            <div className="modal-buttons">
              <button className="danger-button" onClick={handleDeleteContract}>
                Yes, Delete
              </button>
              <button
                className="secondary-button"
                onClick={() => setShowDeleteConfirmation(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;